import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PageMeta from "../components/PageMeta";

const included = [
  "Custom 4 page website designed for your business",
  "Looks perfect on phones, tablets, and desktops",
  "Contact form that sends inquiries straight to you",
  "Basic SEO so customers can find you on Google",
  "Fast and secure with SSL certificate included",
  "Live in 2 to 4 weeks, often sooner"
];

const steps = [
  { num: "01", title: "Free 30 minute call", description: "Tell us about your business. We map out exactly what your website needs. No jargon, no pressure." },
  { num: "02", title: "We design & build", description: "You review the design, request changes, and approve it. Revisions are included." },
  { num: "03", title: "You go live", description: "We launch your site, train you on updates, and stay available if anything needs fixing." }
];

const proof = [
  { title: "Fonderx Exchange", category: "Fintech", image: "/assets/portfolio/fonderx.png", url: "https://fonderx.com" },
  { title: "Maple Kids Academy", category: "Childcare", image: "/assets/portfolio/maple-kids.png", url: "https://mkids.ca" },
  { title: "Vanguard PM", category: "Contracting", image: "/assets/portfolio/vanguard-pm.png", url: "https://vanguardpm.ca" },
  { title: "VanSign", category: "Signage", image: "/assets/portfolio/vansign.png", url: "https://vansign.ca" }
];

export default function Launch() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="Your First Website for $500 | Averoan Technologies"
        description="Professional 4 page website for $500. Custom design, mobile friendly, live in 2 to 4 weeks. Hosting $20 per month. Vancouver, BC."
      />
      <Navbar />

      {/* Hero */}
      <section className="min-h-[90vh] flex items-center relative overflow-hidden pt-20" data-testid="launch-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F11] via-transparent to-[#0F0F11]" />
        <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        <div className="container-main relative z-10 py-16">
          <div className="max-w-4xl">
            <div className="mb-8">
              <span className="tag">Limited Intro Offer</span>
            </div>
            <h1
              className="text-5xl sm:text-6xl md:text-8xl font-bold mb-8 leading-[0.95]"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Your first website<br />
              for <span style={{ color: '#D4FF00' }}>$500</span>
            </h1>
            <p className="text-lg md:text-xl max-w-xl mb-4" style={{ color: '#A1A1AA' }}>
              A professional 4 page website, custom designed for your business.
              Hosting from $20 per month. Live in as little as 2 weeks.
            </p>
            <p className="text-sm mb-10" style={{ color: '#71717A' }}>
              No templates. No hidden fees. 50% to start and 50% when you love it.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/consultation" className="btn-primary" data-testid="launch-cta-primary">
                Book Your Free Call <ArrowRight size={16} />
              </Link>
              <Link to="/portfolio" className="btn-outline" data-testid="launch-cta-secondary">
                See Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding" style={{ background: '#18181B' }} data-testid="launch-included">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="tag mb-4">What You Get</span>
              <h2 className="text-3xl md:text-5xl font-bold mt-4 mb-6" style={{ fontFamily: '"Clash Display", sans-serif' }}>
                Everything included<br />for <span style={{ color: '#D4FF00' }}>$500</span>
              </h2>
              <p className="text-base mb-8" style={{ color: '#A1A1AA' }}>
                This is a complete, professional website. The same quality we deliver
                to every client, at an introductory price for first time customers.
              </p>
              <ul className="space-y-4">
                {included.map((item) => (
                  <li key={item} className="flex items-start gap-3" style={{ color: '#E4E4E7' }}>
                    <Check size={18} style={{ color: '#D4FF00', marginTop: '3px', flexShrink: 0 }} />
                    <span className="text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <div className="pricing-card relative">
                <h3 className="text-xl font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>Hosting</h3>
                <div className="mb-3">
                  <span className="text-3xl font-bold">$20</span>
                  <span style={{ color: '#A1A1AA' }}>/month</span>
                </div>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>Managed hosting with SSL, daily backups, and uptime monitoring.</p>
              </div>
              <div className="pricing-card relative">
                <h3 className="text-xl font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>Care & Support <span className="text-sm font-normal" style={{ color: '#A1A1AA' }}>(optional)</span></h3>
                <div className="mb-3">
                  <span className="text-3xl font-bold">$50</span>
                  <span style={{ color: '#A1A1AA' }}>/month</span>
                </div>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>We troubleshoot and fix anything that breaks, fast.</p>
              </div>
              <div className="pricing-card relative">
                <p className="text-sm" style={{ color: '#A1A1AA' }}>
                  30 days of free support after launch. Cancel monthly plans anytime, no contracts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding" data-testid="launch-steps">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="tag mb-4">How It Works</span>
            <h2 className="text-3xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Three steps to <span style={{ color: '#D4FF00' }}>launch</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((step) => (
              <div key={step.num} className="pricing-card relative">
                <div className="text-5xl font-bold mb-4" style={{ fontFamily: '"Clash Display", sans-serif', color: 'transparent', WebkitTextStroke: '1px #D4FF00' }}>
                  {step.num}
                </div>
                <h3 className="text-xl font-bold mb-3 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{step.title}</h3>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="section-padding" style={{ background: '#18181B' }} data-testid="launch-proof">
        <div className="container-main">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="tag mb-4">Real Clients</span>
              <h2 className="text-3xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>
                Sites we've <span style={{ color: '#D4FF00' }}>shipped</span>
              </h2>
            </div>
            <Link to="/portfolio" className="flex items-center gap-2 text-sm font-medium hover:text-[#D4FF00] transition-colors" style={{ color: '#A1A1AA' }}>
              View All <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {proof.map((project) => (
              <a
                key={project.title}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-xl overflow-hidden"
                style={{ background: '#0F0F11', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <div className="overflow-hidden">
                  <img src={project.image} alt={project.title} className="w-full h-auto transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: '#D4FF00' }}>{project.category}</span>
                  <h3 className="text-sm font-bold normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{project.title}</h3>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding relative overflow-hidden" data-testid="launch-final-cta">
        <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-50" />
        <div className="container-main relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Ready for your<br /><span style={{ color: '#D4FF00' }}>$500 website?</span>
            </h2>
            <p className="text-base md:text-lg mb-10 max-w-lg mx-auto" style={{ color: '#A1A1AA' }}>
              Book a free 30 minute call. If we're not the right fit, you'll still walk away
              with a clear plan for your website.
            </p>
            <Link to="/consultation" className="btn-primary" data-testid="launch-cta-final">
              Book Your Free Call <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
